import pick from "lodash/pick";
import moment, { Moment } from "moment";

import {
  RecurrencePresetName,
  RecurrenceRule,
  RecurrenceRuleType,
  Weekday
} from "@/typings/calendarEvent";
import { Option } from "@/typings/globals";

export function currentWeekday() {
  return parseInt(moment().format("d")) as Weekday;
}

export class RecurrencePreset {
  name: RecurrencePresetName;
  label: string;
  interval?: number = 1;
  type: RecurrenceRuleType;
  days?: Array<Weekday | number> = [];
  ends?: "never" | "on" | "after" = "never";
  until?: Date = new Date();
  count?: number = 1;
  occurrence?: number;
  constructor(params: Partial<RecurrencePreset>) {
    Object.assign(this, params);
  }
  subtitle(date: Moment) {
    switch (this.name) {
      case RecurrencePresetName.weekdays:
        return "Mon-Fri";
      case RecurrencePresetName.weekly:
      case RecurrencePresetName.biweekly:
        return `on ${date.format("ddd")}`;
      case RecurrencePresetName.monthly:
        return `on the ${date.format("Do")}`;
      case RecurrencePresetName.yearly:
        return `on ${date.format("MMM Do")}`;
    }
  }

  toRecurrenceRule() {
    return new RecurrenceRule(
      pick(
        this,
        "type",
        "interval",
        "days",
        "occurrence",
        "ends",
        "until",
        "count"
      )
    );
  }
}

export const recurrencePresets: Array<RecurrencePreset> = [
  new RecurrencePreset({
    name: RecurrencePresetName.daily,
    label: "Every Day",
    interval: 1,
    type: RecurrenceRuleType.DailyRule,
    ends: "never",
    count: 30,
    until: moment().add(30, "days").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.weekdays,
    label: "Every Weekday",
    interval: 1,
    type: RecurrenceRuleType.WeeklyRule,
    days: [
      Weekday.monday,
      Weekday.tuesday,
      Weekday.wednesday,
      Weekday.thursday,
      Weekday.friday
    ],
    ends: "never",
    count: 13,
    until: moment().add(13, "weeks").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.weekly,
    label: "Every Week",
    interval: 1,
    type: RecurrenceRuleType.WeeklyRule,
    ends: "never",
    count: 13,
    until: moment().add(13, "weeks").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.biweekly,
    label: "Every 2 Weeks",
    interval: 2,
    type: RecurrenceRuleType.WeeklyRule,
    ends: "never",
    count: 24,
    until: moment().add(48, "weeks").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.monthly,
    label: "Every Month",
    interval: 1,
    type: RecurrenceRuleType.MonthlyRule,
    ends: "never",
    count: 12,
    until: moment().add(12, "months").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.yearly,
    label: "Every Year",
    interval: 1,
    type: RecurrenceRuleType.YearlyRule,
    ends: "never",
    count: 5,
    until: moment().add(5, "years").toDate()
  }),
  new RecurrencePreset({
    name: RecurrencePresetName.custom,
    label: "Custom",
    interval: 1,
    type: RecurrenceRuleType.WeeklyRule,
    days: [currentWeekday()],
    ends: "never",
    count: 13,
    until: moment().add(13, "weeks").toDate()
  })
];

export function getRecurrencePreset(name: RecurrencePresetName) {
  return recurrencePresets.find((preset) => preset.name === name);
}

export const recurrenceTypeOptions: Array<Option<RecurrenceRuleType>> = [
  {
    label: "Day",
    value: RecurrenceRuleType.DailyRule
  },
  {
    label: "Week",
    value: RecurrenceRuleType.WeeklyRule
  },
  {
    label: "Month",
    value: RecurrenceRuleType.MonthlyRule
  },
  {
    label: "Year",
    value: RecurrenceRuleType.YearlyRule
  }
];

export const dayOptions: Array<Option<Weekday>> = [
  {
    label: "Su",
    value: Weekday.sunday
  },
  {
    label: "Mo",
    value: Weekday.monday
  },
  {
    label: "Tu",
    value: Weekday.tuesday
  },
  {
    label: "We",
    value: Weekday.wednesday
  },
  {
    label: "Th",
    value: Weekday.thursday
  },
  {
    label: "Fr",
    value: Weekday.friday
  },
  {
    label: "Sa",
    value: Weekday.saturday
  }
];
